import { keyframes, styled } from 'styled-components'

import { zIndexes } from '../../theme/zIndexes'
import { BREAKPOINTS } from '../../utils/styled'
import { Typography } from '../Typography'

import type { ModalProps } from './Modal.Types'

export const MODAL_ANIMATE_DURATION = 500

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`

export const StyledOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  z-index: ${zIndexes.modal};
  top: 0;
  left: 0;
  display: flex;
  overflow: hidden;
  width: 100vw;
  height: 100dvh;
  align-items: flex-end;
  justify-content: center;
  padding: ${({ theme }) => theme.sizes.headerHeight}rem 0 0;

  &::before {
    position: absolute;
    z-index: -1;
    animation: ${({ $isOpen }) => ($isOpen ? fadeIn : fadeOut)}
      ${MODAL_ANIMATE_DURATION}ms ease both;
    backdrop-filter: blur(0.25rem);
    background-color: ${({ theme }) => theme.colors['N Surface/Screen Dim']};
    content: '';
    inset: 0;
    pointer-events: none;
  }

  @media ${BREAKPOINTS.MD} {
    align-items: center;
    padding: 1.5rem;
  }
`

export const StyledContainer = styled.div<{
  $backgroundColor: ModalProps['backgroundColor']
  $isOpen: boolean
}>`
  display: flex;
  overflow: auto;
  min-height: 0;
  max-height: 100%;
  flex: 1;
  flex-direction: column;
  border-radius: ${({ theme }) =>
    `${theme.radius['Radius XL']}px ${theme.radius['Radius XL']}px 0 0`};
  animation: ${({ $isOpen }) => ($isOpen ? slideUp : slideDown)}
    ${MODAL_ANIMATE_DURATION}ms ease both;
  background: ${({ $backgroundColor, theme }) =>
    theme.colors[
      $backgroundColor === 'primary' ? 'N Surface/Primary' : 'N Surface/Base'
    ]};
  box-shadow: 0 0 2.25rem 0 rgba(0, 0, 0, 0.24);

  @media ${BREAKPOINTS.MD} {
    max-width: ${({ theme }) =>
      `calc(${theme.sizes.contentWidth}rem - 2 * ${theme.sizes.contentHorizontalPadding}rem)`};
    border-radius: ${({ theme }) => theme.radius['Radius XL']}px;
    animation: ${({ $isOpen }) => ($isOpen ? fadeIn : fadeOut)}
      ${MODAL_ANIMATE_DURATION}ms ease both;
  }
`

export const StyledHeader = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  justify-content: space-between;
  padding: 1rem 1rem 0.5rem;
  gap: 0.5rem;
`

export const StyledHeaderTypography = styled(Typography)`
  padding: 0.25rem 0;
`

export const StyledContent = styled.div`
  display: flex;
  overflow: auto;
  flex: 1;
  flex-direction: column;
  padding: 0 1rem max(1rem, env(safe-area-inset-bottom, 0rem));
`

export const StyledBottomContent = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 1rem;
  margin-top: auto;
  gap: 1rem;
`
